import './MediaDropzone.css';
import React, { useCallback, useId, useMemo, useRef, useState } from 'react';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import CloseIcon from '@material-ui/icons/Close';
import PhotoOutlinedIcon from '@material-ui/icons/PhotoOutlined';
import API from '../../api/api';
import uuid from 'uuid';
import { getImageUrl } from '../../utils/imageUtils';

const garageApi = new API();

const SUPPORTED = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

export function formatUploadError(error) {
  const data = error?.response?.data;
  if (data?.message) return data.message;
  if (data?.debug?.post_max_size_reached) {
    return 'Datei zu gross für den Server.';
  }
  if (error?.response?.status === 401) return 'Nicht angemeldet — bitte erneut einloggen.';
  if (error?.response?.status === 413) return 'Datei zu gross (Server-Limit).';
  return error?.message || 'Upload fehlgeschlagen';
}

async function uploadFile(file, { type, name, maxSizeMB }) {
  if (!file.type.startsWith('image/')) {
    throw new Error(`${file.name}: Keine Bilddatei`);
  }
  if (!SUPPORTED.includes(file.type.toLowerCase())) {
    throw new Error(`${file.name}: Nur JPG, PNG, GIF oder WebP`);
  }
  if (file.size > maxSizeMB * 1024 * 1024) {
    throw new Error(`${file.name}: Max. ${maxSizeMB} MB`);
  }

  const formData = new FormData();
  formData.append('image', file);
  formData.append('type', type);
  formData.append('name', name);
  formData.append('unikey', uuid.v4());

  const response = await garageApi.endpoints.image.create(formData, {
    timeout: 120000,
    maxContentLength: Infinity,
    maxBodyLength: Infinity,
  });

  if (!response?.data?.id) {
    throw new Error(`${file.name}: Ungültige Server-Antwort`);
  }

  return {
    id: response.data.id,
    image: response.data.image,
    fileName: file.name,
  };
}

function previewUrl(item) {
  if (!item) return null;
  if (typeof item === 'string') return item;
  return getImageUrl(item, 'medium') || getImageUrl(item, 'small') || getImageUrl(item, 'large');
}

/**
 * Einheitliche Admin-Dropzone.
 * multiple=false: ein Bild (ersetzen/entfernen)
 * multiple=true: mehrere Bilder (Grid + löschen)
 */
export default function MediaDropzone({
  multiple = false,
  label = 'Bild hinzufügen',
  hint = 'JPG, PNG, GIF oder WebP',
  type = 'default',
  name = 'Image',
  maxSizeMB = 64,
  // single
  value = null,
  onChange,
  onClear,
  // multi
  images = [],
  onImagesChange,
  onError,
  disabled = false,
}) {
  const inputId = useId();
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState(null);
  const [localPreview, setLocalPreview] = useState(null);

  const singleUrl = useMemo(() => localPreview || previewUrl(value), [localPreview, value]);
  const list = multiple ? images : [];

  const runUpload = useCallback(
    async (fileList) => {
      const files = Array.from(fileList || []);
      if (!files.length || disabled) return;

      setError(null);
      setUploading(true);

      try {
        if (!multiple) {
          const file = files[0];
          const reader = new FileReader();
          reader.onloadend = () => setLocalPreview(reader.result);
          reader.readAsDataURL(file);

          const uploaded = await uploadFile(file, { type, name, maxSizeMB });
          setLocalPreview(null);
          onChange?.(uploaded);
        } else {
          const next = [...images];
          setProgress({ done: 0, total: files.length });
          const errors = [];

          for (let i = 0; i < files.length; i += 1) {
            setProgress({ done: i, total: files.length });
            try {
              next.push(await uploadFile(files[i], { type, name, maxSizeMB }));
            } catch (err) {
              errors.push(formatUploadError(err));
            }
          }

          setProgress({ done: files.length, total: files.length });
          onImagesChange?.(next);
          if (errors.length) {
            const msg = errors.join(' · ');
            setError(msg);
            onError?.(new Error(msg));
          }
        }
      } catch (err) {
        setLocalPreview(null);
        const msg = formatUploadError(err);
        setError(msg);
        onError?.(err);
      } finally {
        setUploading(false);
        if (inputRef.current) inputRef.current.value = '';
      }
    },
    [disabled, images, maxSizeMB, multiple, name, onChange, onError, onImagesChange, type]
  );

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    if (disabled || uploading) return;
    runUpload(e.dataTransfer.files);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !uploading) setDragging(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragging(false);
  };

  const clearSingle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setLocalPreview(null);
    setError(null);
    onClear?.();
    onChange?.(null);
  };

  const removeMulti = (id) => {
    onImagesChange?.(images.filter((img) => img.id !== id));
  };

  const zoneClass = [
    'spg-dropzone',
    dragging ? 'is-dragging' : '',
    uploading ? 'is-uploading' : '',
    disabled ? 'is-disabled' : '',
    !multiple && singleUrl ? 'has-preview' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="spg-media">
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        multiple={multiple}
        disabled={disabled || uploading}
        className="spg-dropzone__input"
        onChange={(e) => runUpload(e.target.files)}
      />

      <label
        htmlFor={inputId}
        className={zoneClass}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
      >
        {!multiple && singleUrl ? (
          <div className="spg-dropzone__preview">
            <img src={singleUrl} alt="" />
            <div className="spg-dropzone__preview-mask">
              <span>{uploading ? 'Wird ersetzt…' : 'Klicken oder ablegen zum Ersetzen'}</span>
            </div>
            {!uploading && (
              <button
                type="button"
                className="spg-dropzone__remove"
                onClick={clearSingle}
                aria-label="Bild entfernen"
              >
                <CloseIcon fontSize="small" />
              </button>
            )}
          </div>
        ) : (
          <div className="spg-dropzone__empty">
            <span className="spg-dropzone__icon" aria-hidden>
              {uploading ? (
                <span className="spg-dropzone__spinner" />
              ) : (
                <CloudUploadOutlinedIcon />
              )}
            </span>
            <strong className="spg-dropzone__title">
              {uploading
                ? multiple && progress.total > 1
                  ? `Hochladen… ${progress.done}/${progress.total}`
                  : 'Wird hochgeladen…'
                : label}
            </strong>
            <span className="spg-dropzone__hint">
              {dragging ? 'Jetzt loslassen' : `${hint} · max. ${maxSizeMB} MB`}
            </span>
          </div>
        )}

        {uploading && multiple && progress.total > 1 && (
          <div
            className="spg-dropzone__bar"
            style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }}
          />
        )}
      </label>

      {error && <p className="spg-media__error">{error}</p>}

      {multiple && list.length > 0 && (
        <ul className="spg-media__grid">
          {list.map((img) => {
            const url = previewUrl(img);
            return (
              <li key={img.id || img.unikey} className="spg-media__tile">
                {url ? (
                  <img src={url} alt={img.fileName || ''} />
                ) : (
                  <span className="spg-media__tile-fallback">
                    <PhotoOutlinedIcon fontSize="small" />
                  </span>
                )}
                <button
                  type="button"
                  className="spg-dropzone__remove"
                  onClick={() => removeMulti(img.id)}
                  aria-label="Bild entfernen"
                >
                  <CloseIcon fontSize="small" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
